import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BadgeCheck,
  BookOpen,
  Check,
  Crown,
  Infinity,
  Layers3,
  LockKeyhole,
  School,
  Sparkles,
  Target,
  WalletCards,
  Zap
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import api from "../services/api";
import { getToken } from "../services/auth";
import { jwtDecode } from "jwt-decode";

const planIcons = {
  free_courses: BookOpen,
  paid_courses: WalletCards,
  monthly_subscription: Zap,
  lifetime_access: Infinity,
  freemium: Layers3,
};

export default function SubscriptionPlans() {
  const navigate = useNavigate();
  const [role, setRole] = useState("student");
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [taxonomy, setTaxonomy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyPlan, setBusyPlan] = useState(null);

  useEffect(() => {
    const token = getToken();
    if (token) {
      try {
        setRole(jwtDecode(token).role || "student");
      } catch { }
    }

    Promise.all([
      api.get("/subscriptions/plans"),
      api.get("/subscriptions/taxonomy"),
      api.get("/subscriptions/me").catch(() => ({ data: null })),
    ])
      .then(([plansRes, taxonomyRes, subRes]) => {
        setPlans(plansRes.data || []);
        setTaxonomy(taxonomyRes.data || null);
        setSubscription(subRes.data);
      })
      .catch(() => toast.error("Failed to load subscription plans"))
      .finally(() => setLoading(false));
  }, []);

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleChoosePlan = async (plan) => {
    if (plan.key === "paid_courses") {
      navigate("/public-courses");
      return;
    }

    setBusyPlan(plan.key);
    try {
      if (!plan.price) {
        const res = await api.post("/subscriptions/activate", { plan_key: plan.key });
        setSubscription(res.data);
        toast.success(`${plan.name} activated`);
        return;
      }

      const ready = await loadRazorpay();
      if (!ready) {
        toast.error("Payment system failed to load");
        return;
      }

      const [orderRes, configRes] = await Promise.all([
        api.post("/payments/create-order", { amount: plan.price }),
        api.get("/payments/config").catch(() => ({ data: {} }))
      ]);
      const order = orderRes.data;
      const rzpKey = configRes.data.razorpay_key || "rzp_test_ShJUkFoOhGho5K";

      const paymentObject = new window.Razorpay({
        key: rzpKey,
        amount: order.amount,
        currency: "INR",
        name: "Antigravity Learning",
        description: plan.name,
        order_id: order.id,
        handler: async (response) => {
          try {
            const res = await api.post("/subscriptions/verify-payment", {
              plan_key: plan.key,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setSubscription(res.data);
            toast.success(`${plan.name} activated`);
          } catch {
            toast.error("Subscription payment verification failed");
          }
        },
        prefill: {
          name: "Student",
          email: "student@example.com",
        },
        theme: { color: "#4f46e5" },
      });

      paymentObject.open();
    } catch {
      toast.error("Unable to activate plan");
    } finally {
      setBusyPlan(null);
    }
  };

  const currentPlan = plans.find(plan => plan.key === subscription?.plan_key);

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-950">
      <Toaster position="top-center" />
      
      <main className="flex-1 p-4 sm:p-6 lg:p-10 min-w-0">
        <section className="relative overflow-hidden rounded-lg bg-slate-950 px-5 py-8 text-white shadow-2xl shadow-slate-900/10 sm:px-8 lg:px-10">
          <div className="absolute right-0 top-0 h-48 w-48 translate-x-16 -translate-y-16 rounded-full bg-indigo-500/20 blur-3xl" />
          <div className="relative z-10 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-xs font-black uppercase tracking-widest text-indigo-100">
                <Sparkles size={14} /> Student Access
              </div>
              <h1 className="text-3xl font-black tracking-tight sm:text-4xl">Subscription Plans</h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300 sm:text-base">
                Pick the access model that matches how you learn: free courses, paid courses, monthly subscription, lifetime access, or freemium.
              </p>
            </div>

            <div className="rounded-lg border border-white/10 bg-white/10 p-4 backdrop-blur">
              <p className="text-xs font-black uppercase tracking-widest text-slate-400">Current Plan</p>
              <div className="mt-2 flex items-center gap-2 text-lg font-black">
                <Crown size={20} className="text-amber-300" />
                {currentPlan?.name || "No active plan"}
              </div>
              {subscription?.expires_at && (
                <p className="mt-1 text-xs text-slate-300">
                  Renews or expires on {new Date(subscription.expires_at).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        </section>

        {taxonomy && (
          <section className="mt-8 grid gap-5 xl:grid-cols-3">
            <div className="surface p-5 sm:p-6">
              <div className="mb-5 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50 text-amber-600 dark:bg-amber-950/50 dark:text-amber-300">
                  <Target size={20} />
                </span>
                <div>
                  <h2 className="text-lg font-black text-slate-950 dark:text-white">Beginner to Advanced</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Choose by learning depth</p>
                </div>
              </div>
              <div className="space-y-3">
                {taxonomy.levels.map(level => (
                  <div key={level.key} className="rounded-lg border border-slate-200 p-3 dark:border-slate-800">
                    <p className="font-black text-slate-900 dark:text-white">{level.name}</p>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{level.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="surface p-5 sm:p-6">
              <div className="mb-5 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 dark:bg-indigo-950/50 dark:text-indigo-300">
                  <School size={20} />
                </span>
                <div>
                  <h2 className="text-lg font-black text-slate-950 dark:text-white">Age and Class Categories</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Kids, school, +2, and graduation</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="mb-2 text-xs font-black uppercase tracking-widest text-slate-400">Age-wise</p>
                  <div className="flex flex-wrap gap-2">
                    {taxonomy.age_categories.map(item => (
                      <span key={item} className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200">{item}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="mb-2 text-xs font-black uppercase tracking-widest text-slate-400">Class-wise</p>
                  <div className="flex flex-wrap gap-2">
                    {taxonomy.class_categories.map(item => (
                      <span key={item} className="rounded-full bg-indigo-50 px-3 py-1.5 text-xs font-bold text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300">{item}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="surface p-5 sm:p-6">
              <div className="mb-5 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-300">
                  <Layers3 size={20} />
                </span>
                <div>
                  <h2 className="text-lg font-black text-slate-950 dark:text-white">Skill Tracks</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Focused paths for outcomes</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {taxonomy.skill_tracks.map(item => (
                  <span key={item} className="rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">{item}</span>
                ))}
              </div>
            </div>
          </section>
        )}

        {taxonomy && (
          <section className="mt-8">
            <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-xl font-black text-slate-950 dark:text-white">Featured Learning Paths</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">Popular categories students can start with.</p>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {taxonomy.featured_paths.map(path => (
                <article key={path.name} className="surface p-4">
                  <p className="text-xs font-black uppercase tracking-widest text-indigo-500">{path.category}</p>
                  <h3 className="mt-2 text-lg font-black text-slate-950 dark:text-white">{path.name}</h3>
                  <p className="mt-3 text-sm font-semibold text-slate-500 dark:text-slate-400">{path.level}</p>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{path.track}</p>
                </article>
              ))}
            </div>
          </section>
        )}

        {loading ? (
          <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {[1, 2, 3].map(i => <div key={i} className="surface h-72 animate-pulse" />)}
          </div>
        ) : (
          <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {plans.map((plan) => {
              const Icon = planIcons[plan.key] || BadgeCheck;
              const active = subscription?.plan_key === plan.key;
              return (
                <article
                  key={plan.key}
                  className={`surface relative flex flex-col overflow-hidden p-5 sm:p-6 ${plan.highlight ? "border-indigo-300 ring-2 ring-indigo-500/10 dark:border-indigo-700" : ""}`}
                >
                  {plan.highlight && (
                    <div className="absolute right-4 top-4 rounded-full bg-indigo-600 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-white">
                      Popular
                    </div>
                  )}

                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 dark:bg-indigo-950/60 dark:text-indigo-300">
                    <Icon size={24} />
                  </div>

                  <p className="text-xs font-black uppercase tracking-widest text-slate-400">{plan.model}</p>
                  <h2 className="mt-2 text-2xl font-black text-slate-950 dark:text-white">{plan.name}</h2>
                  <p className="mt-3 min-h-[3rem] text-sm leading-6 text-slate-500 dark:text-slate-400">{plan.description}</p>

                  <div className="my-6">
                    {plan.price === null ? (
                      <div className="text-3xl font-black text-slate-950 dark:text-white">Course price</div>
                    ) : plan.price === 0 ? (
                      <div className="text-3xl font-black text-emerald-600">Free</div>
                    ) : (
                      <div className="flex items-end gap-1">
                        <span className="text-3xl font-black text-slate-950 dark:text-white">₹{plan.price}</span>
                        <span className="pb-1 text-sm font-bold text-slate-400">/{plan.billing}</span>
                      </div>
                    )}
                  </div>

                  <div className="mb-6 space-y-3">
                    {plan.features.map((feature) => (
                      <div key={feature} className="flex items-start gap-3 text-sm font-medium text-slate-600 dark:text-slate-300">
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-300">
                          <Check size={13} />
                        </span>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => handleChoosePlan(plan)}
                    disabled={busyPlan === plan.key || active}
                    className={`mt-auto inline-flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-black transition-all disabled:cursor-not-allowed disabled:opacity-70 ${active
                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300"
                      : plan.highlight
                        ? "bg-indigo-600 text-white shadow-lg shadow-indigo-600/20 hover:bg-indigo-700"
                        : "bg-slate-950 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
                      }`}
                  >
                    {busyPlan === plan.key ? (
                      <span className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    ) : active ? (
                      <><BadgeCheck size={18} /> Active Plan</>
                    ) : plan.key === "paid_courses" ? (
                      <><LockKeyhole size={18} /> Browse Paid Courses</>
                    ) : (
                      <>Choose Plan</>
                    )}
                  </button>
                </article>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
